import Janus from 'janus-gateway';
import { buildAssistanceMessage, parseAssistanceMessage, serializeAssistanceMessage } from './assistance-messages.js';

let janusLibraryReady = false;

function ensureJanusLibrary (onReady, onError) {
	if (janusLibraryReady) {
		onReady();
		return;
	}
	if (!Janus.isWebrtcSupported()) {
		onError('WebRTC is not supported in this browser.');
		return;
	}
	Janus.init({
		debug: process.env.NODE_ENV === 'development' ? 'all' : false,
		callback: () => {
			janusLibraryReady = true;
			onReady();
		},
	});
}

function randomTx () {
	return Janus.randomString(12);
}

/**
 * Janus TextRoom client for the assistance channel (WebRTC DataChannel).
 * Room id = appointmentId. Public messages carry the assistance JSON protocol in `text`.
 */
export class AssistanceTextRoomClient {
	/**
	 * @param {(connected: boolean) => void} onConnection
	 * @param {(msg: import("./assistance-messages.js").AssistanceMessage) => void} onMessage
	 * @param {(username: string, display: string) => void} [onParticipantJoin]
	 * @param {(username: string) => void} [onParticipantLeave]
	 */
	constructor (onConnection, onMessage, onParticipantJoin, onParticipantLeave) {
		this.onConnection = onConnection;
		this.onMessage = onMessage;
		this.onParticipantJoin = onParticipantJoin;
		this.onParticipantLeave = onParticipantLeave;

		this.janus = null;
		this.handle = null;
		this.roomId = null;
		this.username = null;
		this.displayName = null;
		this.role = 'clinician';
		this.userId = null;
		this.joined = false;
		/** @type {Map<string, string>} */
		this.participants = new Map();
		/** @type {Map<string, (json: object) => void>} */
		this.transactions = new Map();
	}

	/**
	 * @param {string} serverUrl
	 * @param {number} roomId
	 * @param {string} displayName
	 * @param {{ role?: "operator" | "clinician", userId?: number }} [opts]
	 */
	connect (serverUrl, roomId, displayName, opts = {}) {
		this.disconnect();
		this.roomId = roomId;
		this.displayName = displayName;
		this.role = opts.role ?? 'clinician';
		this.userId = opts.userId ?? null;
		this.username = randomTx();

		ensureJanusLibrary(
			() => this.openSession(serverUrl),
			() => {},
		);
	}

	disconnect () {
		this.joined = false;
		this.participants.clear();
		this.transactions.clear();
		this.onConnection(false);

		const handle = this.handle;
		this.handle = null;

		if (handle) {
			try {
				handle.detach();
			} catch {
				/* ignore */
			}
		}

		if (this.janus) {
			try {
				this.janus.destroy();
			} catch {
				/* ignore */
			}
			this.janus = null;
		}
	}

	/** @param {import("./assistance-messages.js").AssistanceMessage} msg */
	sendAssistance (msg) {
		if (!this.joined || !this.handle || this.roomId == null) return;
		this.sendTextRoom({
			textroom: 'message',
			transaction: randomTx(),
			room: this.roomId,
			text: serializeAssistanceMessage(msg),
			ack: false,
		});
	}

	/** @param {string} text */
	sendChat (text) {
		const trimmed = text.trim();
		if (!trimmed || this.roomId == null) return;
		const msg = buildAssistanceMessage({
			type: 'chat',
			appointmentId: this.roomId,
			from: {
				role: this.role,
				id: this.userId ?? undefined,
				name: this.displayName ?? undefined,
			},
			payload: { text: trimmed },
		});
		this.sendAssistance(msg);
	}

	openSession (serverUrl) {
		this.janus = new Janus({
			server: serverUrl,
			success: () => {
				this.janus.attach({
					plugin: 'janus.plugin.textroom',
					opaqueId: `assist-${randomTx()}`,
					success: (pluginHandle) => {
						this.handle = pluginHandle;
						this.ensureRoomThenSetup();
					},
					error: () => {
						this.disconnect();
					},
					onmessage: (_msg, jsep) => {
						if (jsep && this.handle) {
							this.handle.createAnswer({
								jsep,
								media: { audio: false, video: false, data: true },
								success: (answer) => {
									this.handle.send({ message: { request: 'ack' }, jsep: answer });
								},
								error: () => {},
							});
						}
					},
					ondataopen: () => {
						this.joinRoom();
					},
					ondata: (data) => this.handleDataChannel(data),
					oncleanup: () => {
						this.joined = false;
						this.onConnection(false);
					},
				});
			},
			error: () => {
				this.disconnect();
			},
		});
	}

	ensureRoomThenSetup () {
		const handle = this.handle;
		if (!handle || this.roomId == null) return;

		handle.send({
			message: { request: 'exists', room: this.roomId },
			success: (result) => {
				if (result?.exists === true) {
					this.sendSetup();
					return;
				}
				handle.send({
					message: {
						request: 'create',
						room: this.roomId,
						description: `Assistance #${this.roomId}`,
						is_private: false,
						permanent: false,
					},
					success: () => this.sendSetup(),
					error: () => this.sendSetup(),
				});
			},
			error: () => this.sendSetup(),
		});
	}

	sendSetup () {
		if (this.handle) {
			this.handle.send({
				message: { request: 'setup' },
				error: () => {},
			});
		}
	}

	joinRoom () {
		if (!this.handle || this.roomId == null || !this.username) return;

		const tx = randomTx();
		this.transactions.set(tx, (json) => {
			if (json.textroom === 'error') return;
			this.joined = true;
			this.onConnection(true);

			const presence = buildAssistanceMessage({
				type: 'presence',
				appointmentId: this.roomId,
				from: {
					role: this.role,
					id: this.userId ?? undefined,
					name: this.displayName ?? undefined,
				},
				payload: { state: 'joined' },
			});
			this.sendAssistance(presence);

			const parts = json.participants;
			if (Array.isArray(parts)) {
				for (const p of parts) {
					if (p?.username) this.participants.set(p.username, p.display || p.username);
				}
			}
		});

		this.sendTextRoom({
			textroom: 'join',
			transaction: tx,
			room: this.roomId,
			username: this.username,
			display: this.displayName ?? this.username,
			history: false,
		});
	}

	/** @param {object} body */
	sendTextRoom (body) {
		if (this.handle) {
			this.handle.data({
				text: JSON.stringify(body),
				error: () => {},
			});
		}
	}

	/** @param {string} data */
	handleDataChannel (data) {
		let json;
		try {
			json = JSON.parse(data);
		} catch {
			return;
		}

		const tx = json.transaction;
		if (tx && this.transactions.has(tx)) {
			this.transactions.get(tx)(json);
			this.transactions.delete(tx);
			return;
		}

		const what = json.textroom;
		if (what === 'message') {
			const parsed = parseAssistanceMessage(json.text);
			if (parsed) {
				// Drop our own chat echo — the UI already appended it optimistically.
				if (parsed.type === 'chat' && parsed.from.role === this.role) return;
				this.onMessage(parsed);
			}
			return;
		}

		if (what === 'join') {
			const u = json.username;
			if (u) {
				this.participants.set(u, json.display || u);
				if (this.onParticipantJoin) this.onParticipantJoin(u, json.display || u);
			}
			return;
		}

		if (what === 'leave') {
			const u = json.username;
			if (u) {
				this.participants.delete(u);
				if (this.onParticipantLeave) this.onParticipantLeave(u);
			}
			return;
		}

		if (what === 'destroyed' && Number(json.room) === Number(this.roomId)) {
			this.disconnect();
		}
	}
}
